import { ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import Lottie from 'react-lottie';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { CharacterChip } from '~/components/Chat';
import { ChatRoomName } from '~/components/Chat/MissionGame/ChatRoomCard/ChatRoomCard';
import { ChatRoomErrorBoundary } from '~/components/ErrorBoundary';
import { Icon, Toast } from '~/components/atomic';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { Header } from '~/components/common/Header';
import { useGetGameChatroomInfoQuery } from '~/features/missionGame';
import { useCommonChatState, useGetChatList, useWebSocket } from '~/hooks';
import { MessageResponse } from '~/types';
import {
  ChatRoomMemberData,
  GameChatRoomData,
  GetGameChatroomInfoResponse,
} from '~/types/missionGameApi';
import { useUserStore, useCommonChatStore } from '~/store';
import { RoundedButtonBox } from '.';
import BGMOnLottie from '../../assets/lottieJson/bgm_on.json';
import ChatHistoryModal from '../../components/common/BottomSheet/ChatHistory/ChatHistoryBottomSheet';
import Background from '../ChatConcept/Background';
import Chat from '../ChatConcept/Chat/Chat';

export default function ChatRoomPage() {
  const { chatRoomInfo } = useLocation().state as {
    chatRoomInfo: GameChatRoomData & { gameId: number };
  };

  const GameChatRoomLoadingLayout = () => {
    return (
      <LoadingLayout>
        <ChatRoomName style={{ fontSize: 23 }}>
          {chatRoomInfo.name}
        </ChatRoomName>
        <CharacterChip
          characters={chatRoomInfo.avatar_list}
          style={{ position: 'absolute', bottom: 49, right: 27 }}
          size={64}
        />
        <LoadingText>Loading...</LoadingText>
        <LoadingBackground src={chatRoomInfo.background_file_path} />
      </LoadingLayout>
    );
  };

  return (
    <Suspense fallback={<GameChatRoomLoadingLayout />}>
      <ChatRoomLayout
        chatRoomInfo={chatRoomInfo}
        onLoadingLayout={<GameChatRoomLoadingLayout />}
      />
    </Suspense>
  );
}

const MissionChatHeader = ({
  bgmSrc,
  setIsChatHistoryOpen,
}: {
  bgmSrc: string;
  setIsChatHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // useEffect로 audioRef를 bgm 태그에 연결 유지
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.5;
      if (isPlaying) {
        audio.play().catch((e) => console.log('재생 오류:', e));
      } else {
        audio.pause();
      }
    }
  }, [isPlaying]);

  const toggleBGM = () => {
    setIsPlaying((prev) => {
      if (audioRef.current) {
        if (prev) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch((e) => console.log('재생 오류:', e));
        }
      }
      return !prev;
    });
  };
  return (
    <Header.Root transparentBg style={{ padding: '0 12px 0 4px' }}>
      {bgmSrc !== '' && (
        <audio ref={audioRef} autoPlay loop>
          <source src={bgmSrc} type='audio/mpeg' />
        </audio>
      )}
      <Header.PrevButton />
      <Header.Section gap={8}>
        <Header.Dart transparentBg={false} />
        <RoundedButtonBox>
          <Button onClick={() => setIsChatHistoryOpen(true)}>
            <Icon color='content_light_1' size={23} iconName={'History'} />
          </Button>
        </RoundedButtonBox>
        {bgmSrc !== '' && (
          <RoundedButtonBox>
            <Button onClick={toggleBGM}>
              {isPlaying ? (
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: BGMOnLottie,
                  }}
                  style={{
                    width: '26px',
                    height: '23px',
                  }}
                />
              ) : (
                <Icon color='content_light_1' size={16} iconName={'BGMOff'} />
              )}
            </Button>
          </RoundedButtonBox>
        )}
      </Header.Section>
    </Header.Root>
  );
};
function ChatRoomLayout({
  chatRoomInfo,
}: {
  chatRoomInfo: GameChatRoomData & { gameId: number };
  onLoadingLayout: ReactNode;
}) {
  const gameChatId = chatRoomInfo.game_chat_id;
  const email = useUserStore((state) => state.email);
  const avatars = useCommonChatStore((state) => state.avatarInfo);

  const { data: chatRoomDetail } = useGetGameChatroomInfoQuery({
    game_chat_id: gameChatId.toString(),
    game_id: chatRoomInfo.gameId.toString(),
  }) as unknown as GetGameChatroomInfoResponse;

  const chatRoomMembers = chatRoomDetail?.members;
  const aiMembers: ChatRoomMemberData[] = chatRoomMembers
    ?.filter((member) => member.character_id !== email)
    .map((member) => ({
      id: member.character_id,
      name: member.character_name,
      thumbnail_url: member.character_thumbnail_file_path,
      is_blocked: false,
    }));

  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [socketConnectSuccess, setSocketConnectSuccess] = useState(false);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);

  const { isWaitingChatRoomInfo, setIsWaitingChatRoomInfo } =
    useCommonChatStore((state) => ({
      isWaitingChatRoomInfo: state.isWaitingChatRoomInfo,
      setIsWaitingChatRoomInfo: state.setIsWaitingChatRoomInfo,
    }));

  // 채팅을 위한 웹소켓 연결 작업
  const { webSocket, socketClose, waitingChat, setWaitingChat } = useWebSocket({
    chatType: 'mission',
    fetchDone: true,
    email,
    setMessages,
    socketConnectSuccess,
    setSocketConnectSuccess,
    chatRoomId: gameChatId.toString(),
  });

  const { isLeaveRoomPending, handleSetWaitingChatTrue } = useCommonChatState({
    chatType: 'mission',
    chatRoomId: chatRoomInfo.game_chat_id.toString(),
    email,
    setWaitingChat,
  });

  const { isChatRoomError, title } = useGetChatList({
    gameId: chatRoomInfo.gameId,
    chatroomId: chatRoomInfo.game_chat_id.toString(),
    email,
    socketConnectSuccess,
    setMessages,
    chatType: 'mission',
    successEnter: true,
    initialGameChatRoomInfo: chatRoomInfo,
    chatMemberCharacters: chatRoomMembers,
  });

  useEffect(() => {
    if (socketConnectSuccess) return;
    setIsWaitingChatRoomInfo(true);
  }, [socketConnectSuccess]);

  if (isChatRoomError) {
    return <ChatRoomErrorBoundary />;
  }

  return (
    <>
      {isLeaveRoomPending && <FullScreenLoading />}
      {socketClose && <Toast type='error' text='socket close' />}

      <HelmetHead title='Mission Game Chat' />
      <MissionChatHeader
        bgmSrc={chatRoomInfo.bgm_file_path}
        setIsChatHistoryOpen={setIsChatHistoryOpen}
      />

      <Container>
        {title && messages.length > 0 && (
          <Chat
            webSocket={webSocket}
            messages={messages}
            setMessages={setMessages}
            waitingChat={waitingChat}
            handleSetWaitingChatTrue={handleSetWaitingChatTrue}
            avatars={aiMembers}
          />
        )}
        <Background
          avatarInfo={avatars}
          bgImageUrl={chatRoomInfo.background_file_path}
        />
      </Container>
      <ChatHistoryModal
        messages={messages}
        onClose={() => setIsChatHistoryOpen(false)}
        isOpen={isChatHistoryOpen}
      />
    </>
  );
}

export const Container = styled.main`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const LoadingLayout = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const LoadingBackground = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LoadingText = styled.span`
  position: absolute;
  bottom: 55px;
  left: 27px;
  color: ${({ theme }) => theme.content.content_light_1};
  font-size: 20px;
  font-weight: 500;
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
`;
